import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ButtonModule,
  IconModule,
  SvgIconRegistryService,
  TourGuideService,
} from '@hglobal/common';
import { environment } from '@fb/environments';

import { FloatingButtonComponent } from './components/floating-button/floating-button.component';
import { AuthService } from './services/auth/auth.service';
import {
  AuthMethod,
  FloatingButtonAction,
  FloatingButtonActionType,
  FloatingButtonConfig,
} from './models';
import { TranslationPathService } from './services/translation/translation-path.service';
import { ALL_ICONS } from './icons/generated/icons';
import { DictionariesService } from './services/dictionaries.service';
import { IndexedDBStorageService } from './services/indexed-db-storage.service';
import { TranslateInPipe } from './pipes/translate-in.pipe';

@Component({
  selector: 'pu-app-component',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  imports: [
    FloatingButtonComponent,
    ButtonModule,
    TranslateModule,
    IconModule,
    TranslateInPipe,
  ],
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class AppComponent implements OnChanges, AfterViewInit {
  @Input('authorization-method') authorizationMethod: AuthMethod;
  @Input('authorization-key') authorizationKey: string;

  @Input('refresh-method') refreshMethod: AuthMethod;
  @Input('refresh-key') refreshKey: string;

  @Input() translationPath?: string;
  @Input() language?: string = 'en';
  @Input() config: FloatingButtonConfig;

  @ViewChild('shellContainer') shellContainerElementRef: ElementRef;
  @ViewChild('welcomeTemplate') welcomeTemplateRef: TemplateRef<any>;
  @ViewChild('openHrBotTemplate') openHrBotTemplateRef: TemplateRef<any>;

  #authService = inject(AuthService);
  #translateService = inject(TranslateService);
  #translationPathService = inject(TranslationPathService);
  #dictionariesService = inject(DictionariesService);
  #iconsService = inject(SvgIconRegistryService);
  #tourGuideService = inject(TourGuideService);
  #indexedDBStorageService = inject(IndexedDBStorageService);

  popupOpened = false;
  blockBotButton = environment.blockAiChat;
  showFinanceSupport = environment.shortName === 'pt';

  readonly #defaultActions: FloatingButtonAction[] = [
    { type: FloatingButtonActionType.PlatformSupport },
    { type: FloatingButtonActionType.ItSupport },
    ...(this.showFinanceSupport ? [{ type: FloatingButtonActionType.FinanceSupport }] : []),
    { type: FloatingButtonActionType.OfficeSupport },
    { type: FloatingButtonActionType.EducationSupport },
    { type: FloatingButtonActionType.AiChat, isDisabled: this.blockBotButton },
  ];
  actions: FloatingButtonAction[] = this.#defaultActions;

  @HostBinding('style.zIndex')
  get zIndex() {
    return this.popupOpened ? '1000' : '';
  }

  onPopupOpen(opened: boolean) {
    this.popupOpened = opened;
  }

  ngOnChanges({
    language,
    translationPath,
    authorizationKey,
    authorizationMethod,
    refreshKey,
    refreshMethod,
    config,
  }: SimpleChanges): void {
    if (language || translationPath) {
      const lang = language?.currentValue || this.language;
      this.#translateService.use(lang);

      this.#translationPathService.setTranslationPath(
        translationPath?.currentValue || this.translationPath,
      );
    }

    if (authorizationKey?.currentValue || authorizationMethod?.currentValue) {
      this.#authService.setAuthToken(
        this.authorizationMethod,
        this.authorizationKey,
      );
    }

    if (refreshKey?.currentValue || refreshMethod?.currentValue) {
      this.#authService.setRefreshToken(this.refreshMethod, this.refreshKey);
    }

    if (config?.currentValue) {
      this.#handelConfigChange(config.currentValue);
    }
  }

  ngAfterViewInit(): void {
    this.#setupIcons();

    this.#showTourGuide();
  }

  #setupIcons(): void {
    if (!this.shellContainerElementRef?.nativeElement) {
      return;
    }

    let allIcons = ALL_ICONS;

    for (let iconKey in this.config?.icons) {
      allIcons = allIcons.filter((icon) => icon.id !== iconKey);

      allIcons.push({
        id: iconKey,
        symbol: this.config.icons[iconKey].symbol.replace(
          /id="[^"]*"/g,
          `id="${iconKey}"`,
        ),
      });
    }

    this.#iconsService.addIcons(
      allIcons,
      this.shellContainerElementRef.nativeElement,
    );
  }

  async #showTourGuide(): Promise<void> {
    if (!(await this.#isAlreadyShowed()) && !this.blockBotButton) {
      this.#tourGuideService.setSteps(
        [
          {
            anchorId: 'pu-floating-button__component',
            delay: 1000,
            content: this.welcomeTemplateRef,
            position: 'tc',
            offset: [0, -8],
          },
          {
            anchorId: 'pt-floating-button__ai-chat',
            delay: 500,
            content: this.openHrBotTemplateRef,
            position: ['lc', 'tc'],
            offset: [-8, -8],
          },
        ],
        this.shellContainerElementRef.nativeElement,
      );

      this.#tourGuideService.start();
    }
  }

  async #isAlreadyShowed(): Promise<boolean> {
    await this.#checkLocalStorageFlag();

    const flag = await this.#indexedDBStorageService.getFlag(
      'pu-fb-popularization',
    );

    if (flag) {
      return true;
    }

    await this.#indexedDBStorageService.setFlag('pu-fb-popularization', true);

    return false;
  }

  async #checkLocalStorageFlag(): Promise<void> {
    if (localStorage.getItem('pu-fb-popularization')) {
      await this.#indexedDBStorageService.setFlag('pu-fb-popularization', true);

      return;
    }
  }

  #handelConfigChange(config: FloatingButtonConfig | string): void {
    let parsedConfig;

    if (typeof config === 'string') {
      try {
        parsedConfig = JSON.parse(config);
      } catch (e) {
        console.error('Invalid JSON string in config.currentValue', e);
        parsedConfig = {};
      }
    } else {
      parsedConfig = config;
    }

    this.#dictionariesService.addTranslations(
      parsedConfig.translations,
      this.language || 'en',
    );

    const actions = parsedConfig?.actions;

    if (actions?.length) {
      this.actions = actions;
    }
  }
}
